/**
 * Cálculo de tempo de resolução, SLA (no-prazo/atrasada) e backlog envelhecido — usado pela aba
 * Performance de Relatórios (KPIs, tabela por categoria) e por `calculateStats` (Tempo Médio
 * Global), que importa `computeResolutionDays` daqui. Separado de `reportsCalculations.ts` (que
 * cuida de categoria/tendências/CSV) por ser uma unidade coesa e independente.
 */
import { differenceInDays, parseISO, isValid, format } from 'date-fns';
import { IDemand, IDemandsData, ISystemConfig } from '@/types';

/** Mesmo formato de `ReportDateRangeFilter` (`reportsCalculations.ts`) — redeclarado aqui em vez de
 * importado pra não criar dependência circular entre os dois módulos (`reportsCalculations.ts`
 * reexporta tudo deste arquivo). */
interface DateRangeFilter {
  startDate?: Date | null;
  endDate?: Date | null;
}

/**
 * Data de referência para contar o tempo de uma demanda — mesma regra usada em toda a aba
 * Performance (KPI global, tabela por categoria e timeline):
 * - Demanda recorrente (`recurringDemandId` presente): a partir da data prevista de resolução
 *   (`estimatedCompletion`), OU da data de início (`startedAt`) se o trabalho começou antes do
 *   prazo previsto. `createdAt` de uma demanda recorrente é a data da própria ocorrência (mesma
 *   coisa que `estimatedCompletion` na geração — ver RecurringDemandService), não quando o
 *   problema surgiu, por isso não é usada aqui como referência.
 * - Demanda avulsa: a partir da data de abertura (`createdAt`), como sempre.
 * Retorna `null` quando não há nenhuma data utilizável (não deveria acontecer em uso normal, já
 * que toda demanda tem `createdAt`).
 */
function getResolutionReferenceStart(demand: IDemand): Date | null {
  const isRecurring = Boolean(demand.recurringDemandId);

  if (isRecurring) {
    const estimated = demand.estimatedCompletion
      ? parseISO(demand.estimatedCompletion as unknown as string)
      : null;
    if (estimated && isValid(estimated)) {
      const started = demand.startedAt ? parseISO(demand.startedAt as unknown as string) : null;
      return started && isValid(started) && started < estimated ? started : estimated;
    }
    // Recorrente sem estimatedCompletion (não deveria acontecer, mas não deixa sem referência).
  }

  if (!demand.createdAt) return null;
  const createdAt = parseISO(demand.createdAt as unknown as string);
  return isValid(createdAt) ? createdAt : null;
}

/** Tempo de resolução (em dias) de uma demanda concluída, seguindo `getResolutionReferenceStart`.
 * `null` se a demanda não estiver concluída ou faltar alguma data necessária. */
export function computeResolutionDays(demand: IDemand): number | null {
  if (!demand.completedAt) return null;
  const completedAt = parseISO(demand.completedAt as unknown as string);
  if (!isValid(completedAt)) return null;
  const referenceStart = getResolutionReferenceStart(demand);
  if (!referenceStart) return null;
  return differenceInDays(completedAt, referenceStart);
}

/** Idade (em dias) de uma demanda ainda aberta/em andamento, seguindo a mesma referência de início
 * usada no tempo de resolução. Usada para detectar backlog envelhecido. */
export function computeOpenBacklogAgeDays(demand: IDemand, today: Date = new Date()): number | null {
  const referenceStart = getResolutionReferenceStart(demand);
  if (!referenceStart) return null;
  return differenceInDays(today, referenceStart);
}

/** Uma demanda concluída está "no prazo" quando `completedAt` cai no mesmo dia de
 * `estimatedCompletion` ou antes. Demandas concluídas sem `estimatedCompletion` cadastrada não
 * entram nem em "no prazo" nem em "atrasada" (não há prazo pra violar) — retorna `null`. */
export function isDemandCompletedOnTime(demand: IDemand): boolean | null {
  if (!demand.completedAt || !demand.estimatedCompletion) return null;
  const completedAt = parseISO(demand.completedAt as unknown as string);
  const estimated = parseISO(demand.estimatedCompletion as unknown as string);
  if (!isValid(completedAt) || !isValid(estimated)) return null;
  return differenceInDays(completedAt, estimated) <= 0;
}

/** Demandas abertas/em andamento (não concluídas, não canceladas) que já passam de
 * `agingThresholdDays` de idade (mesma referência de início do tempo de resolução) sem conclusão —
 * ficam invisíveis em "Tempo Médio" porque esse KPI só enxerga demandas concluídas. Base de
 * `getAgingBacklogCount` (contagem) e do drill-down do card "Backlog Envelhecido" (lista). */
export function getAgingBacklogDemands(filteredData: IDemandsData[], agingThresholdDays: number): IDemand[] {
  const today = new Date();
  const result: IDemand[] = [];

  filteredData.forEach((clientData) => {
    const openDemands = [
      ...(clientData.demands.pending || []),
      ...(clientData.demands.in_progress || []),
    ];
    openDemands.forEach((demand) => {
      const age = computeOpenBacklogAgeDays(demand, today);
      if (age !== null && age > agingThresholdDays) {
        result.push(demand);
      }
    });
  });

  return result;
}

/** Quantas demandas abertas/em andamento já passam de `agingThresholdDays` de idade sem conclusão —
 * ver `getAgingBacklogDemands`. */
export function getAgingBacklogCount(filteredData: IDemandsData[], agingThresholdDays: number): number {
  return getAgingBacklogDemands(filteredData, agingThresholdDays).length;
}

export interface PerformanceByCategoryEntry {
  total: number;
  count: number;
  avg: number;
  /** Demandas concluídas com completedAt <= estimatedCompletion. */
  onTime: number;
  /** Demandas concluídas com completedAt > estimatedCompletion. */
  late: number;
  /** IDs de todas as demandas concluídas contabilizadas nesta categoria (drill-down). */
  demandIds: string[];
  /** IDs das concluídas no prazo. */
  onTimeIds: string[];
  /** IDs das concluídas com atraso. */
  lateIds: string[];
}

/** `%` (0-100, 1 casa decimal) de demandas concluídas no prazo, somando todas as categorias. `null`
 * se nenhuma demanda concluída tiver `estimatedCompletion` cadastrada (denominador zero). */
export function getGlobalSlaPercent(
  performanceByCategory: { [key: string]: PerformanceByCategoryEntry },
): number | null {
  let onTime = 0;
  let total = 0;
  Object.values(performanceByCategory).forEach((entry) => {
    onTime += entry.onTime;
    total += entry.onTime + entry.late;
  });
  if (total === 0) return null;
  return Math.round((onTime / total) * 1000) / 10;
}

export interface PerformanceChartRow {
  name: string;
  /** Chave da categoria (ex.: "manutencao") — usada pra cruzar com o período anterior na
   * comparação temporal, já que `name` é o rótulo (pode não bater se o rótulo mudar na config). */
  categoryKey: string;
  dias: number;
  quantidade: number;
  /** Demandas concluídas com completedAt <= estimatedCompletion. */
  noPrazo: number;
  /** Demandas concluídas com completedAt > estimatedCompletion. */
  atrasadas: number;
  /** IDs de todas as demandas concluídas desta categoria (drill-down). */
  demandIds: string[];
  /** IDs das concluídas no prazo. */
  noPrazoIds: string[];
  /** IDs das concluídas com atraso. */
  atrasadasIds: string[];
}

export const buildPerformanceExportRows = (
  performanceChartData: PerformanceChartRow[],
  config: ISystemConfig | null,
) => {
  const performanceThresholds = config?.reports?.performanceThresholds || {
    excellent: 3,
    good: 7,
    regular: 14,
  };

  return performanceChartData.map((row) => {
    const performance =
      row.dias < performanceThresholds.excellent
        ? 'Excelente'
        : row.dias < performanceThresholds.good
        ? 'Bom'
        : row.dias < performanceThresholds.regular
        ? 'Regular'
        : 'Crítico';

    return {
      Categoria: row.name,
      'Demandas Concluídas': row.quantidade,
      'No Prazo (Concluídas)': row.noPrazo,
      'Atrasadas (Concluídas)': row.atrasadas,
      'Tempo Médio (Concluídas) (dias)': row.dias.toFixed(1),
      'Avaliação (Concluídas)': performance,
    };
  });
};

/** Mesmo shape de PerformanceByOperatorEntry (ver ReportsCalculationsService.ts no backend e
 * ReportsPage.tsx no front) — tipado localmente aqui (em vez de importado de ReportsPage.tsx) pra
 * não criar import circular (ReportsPage.tsx importa este arquivo). */
interface PerformanceByOperatorRow {
  operatorLabel: string;
  operatorType: 'operatorName' | 'user' | 'provider' | 'unknown';
  /** Ver PerformanceByOperatorEntry.loginLabel no backend/ReportsPage.tsx. */
  loginLabel?: string;
  startedCount: number;
  completedCount: number;
  cancelledCount: number;
  avgResolutionDays: number;
  onTimeCount: number;
  lateCount: number;
  agingCount: number;
}

const OPERATOR_TYPE_LABEL: Record<PerformanceByOperatorRow['operatorType'], string> = {
  operatorName: 'Nome informado',
  user: 'Login próprio',
  provider: 'Prestador',
  unknown: 'Sem identificação',
};

/** Linhas de exportação (CSV/Excel) da aba "Desempenho da Equipe" — mesmo padrão de
 * buildPerformanceExportRows acima, uma linha por pessoa/prestador em vez de por categoria. */
export const buildPerformanceByOperatorExportRows = (entries: PerformanceByOperatorRow[]) =>
  entries.map((row) => ({
    Pessoa: row.operatorLabel,
    // Pedido do usuário: "quando se tratar de um nome informado também exiba por qual usuário
    // esse nome foi informado" — embutido aqui (não uma coluna nova) pra não quebrar o formato de
    // 9 colunas já usado também na exportação em PDF e nas duas telas (web/mobile).
    Identificação: row.loginLabel
      ? `${OPERATOR_TYPE_LABEL[row.operatorType]} (via ${row.loginLabel})`
      : OPERATOR_TYPE_LABEL[row.operatorType],
    Iniciadas: row.startedCount,
    Concluídas: row.completedCount,
    Canceladas: row.cancelledCount,
    'Tempo Médio (dias)': row.completedCount > 0 ? row.avgResolutionDays.toFixed(1) : '—',
    'No Prazo': row.onTimeCount,
    Atrasadas: row.lateCount,
    'Em aberto há muito tempo': row.agingCount,
  }));

export const getPerformanceAnalysis = (
  filteredData: IDemandsData[],
): { [key: string]: PerformanceByCategoryEntry } => {
  const performanceByCategory: { [key: string]: PerformanceByCategoryEntry } = {};

  filteredData.forEach((clientData) => {
    const completed = clientData.demands.completed || [];
    completed.forEach((demand) => {
      const days = computeResolutionDays(demand);
      if (days === null) return;

      const category = demand.category || 'outros';
      const existing = performanceByCategory[category] || {
        total: 0,
        count: 0,
        avg: 0,
        onTime: 0,
        late: 0,
        demandIds: [],
        onTimeIds: [],
        lateIds: [],
      };

      const onTime = isDemandCompletedOnTime(demand);
      const demandId = demand._id?.toString() || '';

      performanceByCategory[category] = {
        ...existing,
        total: existing.total + days,
        count: existing.count + 1,
        onTime: existing.onTime + (onTime === true ? 1 : 0),
        late: existing.late + (onTime === false ? 1 : 0),
        demandIds: [...existing.demandIds, demandId],
        onTimeIds: onTime === true ? [...existing.onTimeIds, demandId] : existing.onTimeIds,
        lateIds: onTime === false ? [...existing.lateIds, demandId] : existing.lateIds,
      };
    });
  });

  Object.keys(performanceByCategory).forEach((category) => {
    const data = performanceByCategory[category];
    if (data && data.count > 0) {
      data.avg = Math.round((data.total / data.count) * 10) / 10;
    }
  });

  return performanceByCategory;
};

/** Média (em dias) do tempo de resolução de todas as demandas concluídas em `filteredData`, mesma
 * regra recorrente/avulsa de `computeResolutionDays`. Usada tanto por `calculateStats` (Tempo Médio
 * Global do período atual) quanto pelo cálculo do período de comparação — um único lugar pra essa
 * conta evita a mesma lógica duplicada nos dois. */
export function getAverageResolutionDays(filteredData: IDemandsData[]): number {
  const days = filteredData
    .flatMap((clientData) => clientData.demands.completed || [])
    .map((demand) => computeResolutionDays(demand))
    .filter((d): d is number => d !== null);

  if (days.length === 0) return 0;
  return Math.round((days.reduce((sum, d) => sum + d, 0) / days.length) * 10) / 10;
}

/** Converte uma data (string ISO ou Date) pra epoch ms, ou `null` se ausente/inválida. Timestamp
 * "cru" (não normalizado pro início do dia) — usado pra posicionar pontos/barras num eixo de tempo
 * do Chart.js (Linha do Tempo), diferente de `toLocalDayStart` em `reportsCalculations.ts` (que
 * normaliza pra comparação de filtro por dia). */
export function toMs(value: unknown): number | null {
  if (!value) return null;
  const time = new Date(value as string).getTime();
  return Number.isNaN(time) ? null : time;
}

/** As 7 situações de prazo que uma demanda pode estar, usadas na Linha do Tempo e no drill-down
 * (modal "Demandas Relacionadas"): concluída no prazo/com atraso/sem prazo previsto, em andamento
 * dentro/fora do prazo/sem prazo previsto, ou cancelada (fora da contagem de prazo).
 *
 * As variantes `_no_deadline` existem por regra de negócio (21/08/2026): uma demanda sem
 * `estimatedCompletion` cadastrada não pode ser rotulada como "atrasada"/"concluída com atraso" —
 * não há prazo pra violar. Mesmo raciocínio de `isDemandCompletedOnTime` (retorna `null` nesse
 * caso, não `false`), aplicado aqui de forma simétrica: nem "no prazo" nem "atrasada", um terceiro
 * estado neutro — a demanda continua aparecendo normalmente, só sem essa inferência específica. */
export type DeadlineStatusKind =
  | 'completed_on_time'
  | 'completed_late'
  | 'completed_no_deadline'
  | 'in_progress_on_time'
  | 'in_progress_late'
  | 'in_progress_no_deadline'
  | 'cancelled';

export const DEADLINE_STATUS_COLORS: Record<DeadlineStatusKind, string> = {
  completed_on_time: '#4caf50',
  completed_late: '#f44336',
  completed_no_deadline: '#78909c',
  in_progress_on_time: '#2196f3',
  in_progress_late: '#ff9800',
  in_progress_no_deadline: '#78909c',
  cancelled: '#9e9e9e',
};

export const DEADLINE_STATUS_LABELS: Record<DeadlineStatusKind, string> = {
  completed_on_time: 'Concluída no prazo',
  completed_late: 'Concluída com atraso',
  completed_no_deadline: 'Concluída (sem prazo previsto)',
  in_progress_on_time: 'Em andamento, dentro do prazo',
  in_progress_late: 'Em andamento, fora do prazo',
  in_progress_no_deadline: 'Em andamento (sem prazo previsto)',
  cancelled: 'Cancelada',
};

export interface DeadlineStatus {
  kind: DeadlineStatusKind;
  label: string;
  color: string;
}

/** Classifica a situação de prazo de uma demanda (concluída/em andamento/cancelada × dentro/fora do
 * prazo previsto, ou sem prazo previsto cadastrado). Mesma regra usada na Linha do Tempo
 * (`TimelineTab`) e no modal de drill-down (`RelatedDemandsModal`, incluindo o PDF) — um único
 * lugar evita as telas divergirem sobre o que conta como "atrasada". */
export function classifyDemandDeadlineStatus(demand: IDemand, today: Date = new Date()): DeadlineStatus {
  const estimatedMs = toMs(demand.estimatedCompletion);
  const completedMs = toMs(demand.completedAt);
  const cancelledMs = toMs(demand.cancelledAt);

  let kind: DeadlineStatusKind;
  if (completedMs !== null) {
    if (estimatedMs === null) {
      kind = 'completed_no_deadline';
    } else {
      kind = completedMs <= estimatedMs ? 'completed_on_time' : 'completed_late';
    }
  } else if (cancelledMs !== null) {
    kind = 'cancelled';
  } else if (estimatedMs === null) {
    kind = 'in_progress_no_deadline';
  } else {
    kind = today.getTime() > estimatedMs ? 'in_progress_late' : 'in_progress_on_time';
  }

  return { kind, label: DEADLINE_STATUS_LABELS[kind], color: DEADLINE_STATUS_COLORS[kind] };
}

/**
 * Período de comparação: mesma duração do período informado, imediatamente anterior e sem
 * sobreposição (ex.: 01–20/08 → comparação 12/07–31/07). `null` quando o período atual não tem
 * `startDate`/`endDate` definidos (filtro "Todos" — não há uma duração pra replicar).
 */
export function getPreviousPeriodRange(dateRange: DateRangeFilter): { startDate: Date; endDate: Date } | null {
  if (!dateRange.startDate || !dateRange.endDate) return null;

  const currentStart = new Date(dateRange.startDate);
  const currentEnd = new Date(dateRange.endDate);
  const durationMs = currentEnd.getTime() - currentStart.getTime();
  if (durationMs < 0) return null;

  const previousEnd = new Date(currentStart.getTime() - 1); // 1ms antes do início atual, sem sobrepor
  const previousStart = new Date(previousEnd.getTime() - durationMs);

  return { startDate: previousStart, endDate: previousEnd };
}

/**
 * Dados do período anterior pra comparação temporal na aba Performance — computados em
 * `ReportsPage.tsx` (mesma régua de filtro do período atual, só trocando o `dateRange`) e
 * repassados pra `PerformanceTab`/`PerformanceSummaryCards`/`PerformanceDetailTable`/`TimelineTab`.
 * `enabled: false` quando o switch "Comparar com período anterior" está desligado, ou quando o
 * período atual não tem um intervalo definido (`getPreviousPeriodRange` retornou `null`).
 */
export interface PerformanceComparisonData {
  enabled: boolean;
  previousPeriodRange: { startDate: Date; endDate: Date } | null;
  previousPerformanceData: { [key: string]: PerformanceByCategoryEntry };
  previousGlobalSlaPercent: number | null;
  previousAgingBacklogCount: number;
  previousAvgResolutionDays: number;
}

export interface TimelineRow {
  id: string;
  label: string;
  startMs: number;
  endMs: number;
  estimatedMs: number | null;
  statusKind: DeadlineStatusKind;
  isOngoing: boolean;
}

/**
 * Monta uma linha da timeline por demanda — usada tanto pelo gráfico (`TimelineTab`) quanto pela
 * exportação CSV/Excel dele (`buildTimelineExportRows`/`ReportsPage`), pra não duplicar a regra em
 * dois lugares:
 * - Início: `startedAt` se existir, senão `createdAt` (cronologia real, diferente da regra de
 *   contagem de tempo de resolução usada nos KPIs de Performance — aqui é sobre "quando as coisas
 *   aconteceram de fato", não sobre medir performance de forma justa para recorrentes).
 * - Fim: `completedAt` se concluída; `cancelledAt` se cancelada; senão "agora" (ainda em curso).
 * - Indicador de prazo reaproveita `classifyDemandDeadlineStatus` (mesma regra do drill-down).
 * - Rótulo: só o número da demanda — sem o nome do condomínio (Relatórios já exige exatamente 1
 *   condomínio selecionado por vez, então repetir o nome em cada linha só ocupava espaço).
 */
export function buildTimelineRows(filteredData: IDemandsData[]): TimelineRow[] {
  const now = new Date();
  const rows: TimelineRow[] = [];

  filteredData.forEach((clientData) => {
    const allDemands = [
      ...(clientData.demands.pending || []),
      ...(clientData.demands.in_progress || []),
      ...(clientData.demands.completed || []),
      ...(clientData.demands.cancelled || []),
    ];

    allDemands.forEach((demand) => {
      const createdAt = toMs(demand.createdAt);
      if (createdAt === null) return;

      const startedAt = toMs(demand.startedAt);
      const startMs = startedAt ?? createdAt;

      const estimatedMs = toMs(demand.estimatedCompletion);
      const completedMs = toMs(demand.completedAt);
      const cancelledMs = toMs(demand.cancelledAt);
      const { kind: statusKind } = classifyDemandDeadlineStatus(demand, now);
      const isOngoing = completedMs === null && cancelledMs === null;

      const endMs = completedMs ?? cancelledMs ?? now.getTime();
      // Largura mínima visível pra demanda aberta e concluída no mesmo instante.
      const finalEndMs = endMs <= startMs ? startMs + 1000 * 60 * 60 : endMs;

      rows.push({
        id: demand._id,
        label: demand.demandNumber || demand._id?.toString().substring(0, 8) || 'N/A',
        startMs,
        endMs: finalEndMs,
        estimatedMs,
        statusKind,
        isOngoing,
      });
    });
  });

  return rows.sort((a, b) => a.startMs - b.startMs);
}

/** Linhas de exportação (CSV/Excel) da Linha do Tempo — sempre a partir de TODAS as demandas do
 * filtro atual (`buildTimelineRows` sem o teto de exibição nem o filtro da legenda do gráfico, que
 * são só de UI), mesmo padrão dos demais `buildXExportRows` desta pasta. */
export function buildTimelineExportRows(rows: TimelineRow[]) {
  return rows.map((row) => ({
    Demanda: row.label,
    Início: format(new Date(row.startMs), 'dd/MM/yyyy'),
    Término: row.isOngoing ? 'Em aberto/andamento' : format(new Date(row.endMs), 'dd/MM/yyyy'),
    'Prazo Previsto': row.estimatedMs !== null ? format(new Date(row.estimatedMs), 'dd/MM/yyyy') : '—',
    Situação: DEADLINE_STATUS_LABELS[row.statusKind],
  }));
}
